import { Component, ErrorHandler, Inject, ChangeDetectionStrategy, signal } from '@angular/core'
import { form } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSelectChange } from '@angular/material/select'
import { DateService } from '@lemon/core/service/date.service'
import { TimestampProviderBase } from '@lemon/core/service/timestamp-provider.interface'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { TilikarttaService } from 'app/_angular/service/tilikartta.service'
import { asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { Asiakas, Tilikausi } from 'app/_jaettu-lemonator/model/asiakas'
import { Kirjaus, PaatilikartanProfiili } from 'app/_jaettu-lemonator/model/kirjanpito'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'
import { TilikarttaJaettuService } from 'app/_jaettu-lemonator/service/tilikartta-jaettu.service'
import { firstValueFrom, take } from 'rxjs'

export interface TilikartanProfiiliVaihdaDialogData {
  asiakas: Asiakas
  tilikausi: Tilikausi
}

@Component({
  templateUrl: './tilikartan-profiili-vaihda.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TilikartanProfiiliVaihdaDialog {
  nykyinenProfiiliNimi: string
  uusiProfiili = form(signal<PaatilikartanProfiili['avain']>(null))
  readonly tarkistetaan = signal(false)
  readonly virhe = signal<string | null>(null)
  readonly tarkistusOk = signal(false)

  private _kaikkiProfiilit: { nimi: string; tunnus: PaatilikartanProfiili['avain'] }[] = [
    { nimi: 'Kevyt', tunnus: 'maksuperusteinen' },
    { nimi: 'Liikekirjuri', tunnus: 'liikekirjuri' },
  ]
  vaihdettavatProfiilit: { nimi: string; tunnus: PaatilikartanProfiili['avain'] }[]

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: TilikartanProfiiliVaihdaDialogData,
    private _dialogRef: MatDialogRef<TilikartanProfiiliVaihdaDialog>,
    private _firebase: FirebaseLemonator,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _dateService: DateService,
    private _tilikarttaJaettuService: TilikarttaJaettuService,
    private _tilikarttaService: TilikarttaService,
    private _asiakasService: AsiakasService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _timestampService: TimestampProviderBase,
    private _errorHandler: ErrorHandler,
  ) {
    const nykyinenProfiili = this._data.tilikausi.tilikartanProfiili || 'maksuperusteinen'
    this.nykyinenProfiiliNimi = this._kaikkiProfiilit.find(p => p.tunnus === nykyinenProfiili)?.nimi || nykyinenProfiili
    this.vaihdettavatProfiilit = this._kaikkiProfiilit.filter(p => p.tunnus !== nykyinenProfiili)
  }

  async profiiliMuuttui(event: MatSelectChange<PaatilikartanProfiili['avain']>) {
    asetaArvo(this.uusiProfiili, event.value)
    this.tarkistetaan.set(true)
    this.virhe.set(null)
    this.tarkistusOk.set(false)

    try {
      const collectionUri = this._kirjanpitoUriService.annaKirjausCollectionUri(this._data.asiakas.avain)
      const alkuPvm = this._dateService.localDateToNumber(this._data.tilikausi.alkaa) - 1
      const loppuPvm = this._dateService.localDateToNumber(this._data.tilikausi.loppuu) + 1

      const kirjaukset = await this._firebase.firestoreCollection<Kirjaus>(collectionUri).where('p', '>', alkuPvm).where('p', '<', loppuPvm).get()

      const tilinumerot = new Set<string>()
      for (const kirjaus of kirjaukset) {
        if (kirjaus.poistettu) {
          continue
        }
        for (const rivi of kirjaus.rivit || []) {
          if (rivi.t) {
            tilinumerot.add(rivi.t)
          }
        }
      }

      const paatilikartta = await firstValueFrom(this._tilikarttaService.paatilikarttaObservable.pipe(take(1)))
      const asiakkaanTilikartta = await firstValueFrom(this._tilikarttaService.nykyisenAsiakkaanTilikarttaObservable.pipe(take(1)))
      const kohdetilit = this._tilikarttaJaettuService.yhdistaAsiakkaantilikarttaPaatilikarttaanProfiilinAvaimella(paatilikartta, asiakkaanTilikartta, this.uusiProfiili().value())
      const kohdenumerot = new Set(kohdetilit.filter(t => !t.piilotettuProfiilissa).map(t => t.numero))

      const puuttuvat = [...tilinumerot].filter(n => !kohdenumerot.has(n)).sort()

      if (puuttuvat.length > 0) {
        this.virhe.set(`Tilikaudella on vientejä tileille ${puuttuvat.join(', ')}, joita ei löydy tilikartasta. Tilikarttaa ei voi vaihtaa. Poista kirjaukset tai kirjaa viennit muille tileille.`)
      } else {
        this.tarkistusOk.set(true)
      }
    } catch (err) {
      this._errorHandler.handleError(err)
      this.virhe.set('Tarkistuksen aikana tapahtui virhe.')
    } finally {
      this.tarkistetaan.set(false)
    }
  }

  async tallenna() {
    if (!this.tarkistusOk() || this.tarkistetaan()) {
      return
    }

    try {
      const tilikausi = this._data.asiakas.tilikaudet.find(tk => tk.avain === this._data.tilikausi.avain)
      tilikausi.tilikartanProfiili = this.uusiProfiili().value()

      const kirjanpitajanTiedot = await this._kirjautunutKayttajaService.getKirjanpitajanTiedot()

      this._data.asiakas.paivitetty = this._timestampService.now()
      this._data.asiakas.paivittaja = kirjanpitajanTiedot.uid

      const objectToUpdate: Partial<Asiakas> = {
        tilikaudet: this._data.asiakas.tilikaudet,
        paivitetty: this._data.asiakas.paivitetty,
        paivittaja: this._data.asiakas.paivittaja,
      }

      const uri = `/asiakkaat/${this._data.asiakas.avain}`

      const batch = this._firebase.firestoreBatch()
      batch.update(uri, objectToUpdate)
      this._asiakasService.lisaaAsiakkaanHistoriaBatchiin(this._data.asiakas, batch)
      await batch.commit()

      this._dialogRef.close(true)
    } catch (err) {
      this._errorHandler.handleError(err)
    }
  }
}
